/* eslint-disable no-fallthrough */
import { assert } from '@awayjs/graphics';
import { Scope } from './run/Scope';
import { HasNext2Info } from './run/HasNext2Info';
import { validateCall } from './run/validateCall';
import { validateConstruct } from './run/validateConstruct';
import { axCoerceString } from './run/axCoerceString';
import { axCheckFilter } from './run/axCheckFilter';
import { isNumeric, jsGlobal, release } from '@awayfl/swf-loader';
import { Multiname } from './abc/lazy/Multiname';
import { internNamespace } from './abc/lazy/internNamespace';
import { IS_AX_CLASS } from './run/AXClass';
import { axCoerceName } from './run/axCoerceName';
import { Bytecode } from './Bytecode';
import { ASObject } from './nat/ASObject';
import { escapeAttributeValue, escapeElementValue } from './natives/xml';
import { COMPILER_DEFAULT_OPT } from './flags';
// generators
import { analyze } from './gen/analyze';
import { TinyConstructor } from './gen/TinyConstructor';
import { Stat } from './gen/Stat';
import { ComplexGenerator, PhysicsLex, StaticHoistLex, TopLevelLex } from './gen/LexImportsGenerator';
import { emitAnnotation, emitAnnotationOld, emitCloseTryCatch, emitDomainMemOppcodes, emitInlineAccessor as emitAccess, emitInlineLocal, emitInlineMultiname, emitInlineStack, emitOpenTryCatch, emitPrimitiveCoerce, isPrimitiveType, UNDERRUN } from './gen/emiters';
import { emitIsAX, emitIsAXOrPrimitive, extClassConstructor, getExtClassField, IS_EXTERNAL_CLASS, needFastCheck } from './ext/external';
import { Settings } from './Settings';
import { CompilerState } from './gen/CompilerState';
import { TRAITNames } from './abc/lazy/TRAIT';
import { InstanceInfo } from './abc/lazy/InstanceInfo';
import { axConstructFast, isFastConstructSupport } from './run/axConstruct';
var METHOD_HOOKS = {};
export var BytecodeName = Bytecode;
/**
 * Try resolve method and attach hook to it
 */
export function UNSAFE_attachMethodHook(path, place, hook) {
    if (place === void 0) { place = 'begin'; }
    if (!path || typeof hook !== 'function') {
        throw 'Hook path should be exits and function should be a function';
    }
    METHOD_HOOKS[path + '__' + place] = {
        path: path, place: place, hook: hook
    };
}
function generateFunc(body, path) {
    body += "\n//# sourceURL=" + Settings.HTTP_STRING + "jit/" + path + ".js";
    try {
        return new Function('context', body);
    }
    catch (e) {
        throw new Error('Compiler error:\n\n' + body);
    }
}
function findJumpTarget(instrs, jumps, initial, target) {
    var startIndex = -1;
    var endIndex = -1;
    for (var i = initial; i < instrs.length; i++) {
        var inst = instrs[i];
        if (inst.position === target) {
            startIndex = i;
        }
        if (startIndex > i && jumps.indexOf(inst.position) >= 0) {
            endIndex = i;
            break;
        }
    }
    if (startIndex >= 0 && endIndex === -1)
        endIndex = instrs.length;
    return {
        startIndex: startIndex, endIndex: endIndex
    };
}
function isFastReturnVoid(instrs, jumps, initial, target) {
    if (Settings.MAX_INLINE_RETURN <= 0) {
        return false;
    }
    var startIndex = findJumpTarget(instrs, jumps, initial, target).startIndex;
    return startIndex >= 0 && instrs[startIndex].name === Bytecode.RETURNVOID;
}
//@ts-ignore
self.attach_hook = UNSAFE_attachMethodHook;
function resolveTrait(info, name) {
    info.traits.resolve();
    var trait = info.traits.getTrait(name);
    var superInstance = info;
    while (Settings.CHEK_SUPER_TRAITS && !trait && superInstance && superInstance instanceof InstanceInfo) {
        var superName = superInstance.getSuperName();
        if (!superName) {
            return null;
        }
        var addDom = superName.value.applicationDomain;
        var superClass = addDom.getClass(superName);
        superInstance = superClass.classInfo.instanceInfo;
        if (superInstance.runtimeTraits) {
            trait = superInstance.runtimeTraits.getTrait(name.namespaces, name.name);
            if (trait) {
                return trait;
            }
        }
        superInstance.traits.resolve();
        trait = superInstance.traits.getTrait(name);
        if (trait) {
            return trait;
        }
    }
    return trait;
}
export function compile(methodInfo, options) {
    if (options === void 0) { options = {}; }
    var _a, _b;
    var _c = options.optimise, optimise = _c === void 0 ? COMPILER_DEFAULT_OPT : _c, executionScope = options.scope;
    var state = new CompilerState(methodInfo);
    var staticHoistLex = new StaticHoistLex();
    // lex generator
    var lexGen = new ComplexGenerator([
        new PhysicsLex({ box2D: false }),
        new TopLevelLex(),
    ]);
    var tinyCtr = new TinyConstructor();
    // todo
    // 	fastCall not supported now, we change compile flow and it generate wrong results
    // 	remove or refact it
    var fastCall = null; //new FastCall(lexGen, executionScope);
    Stat.begin('');
    var USE_OPT = function (opt) {
        return optimise & 16 /* ALLOW_CUSTOM_OPTIMISER */ && !!opt;
    };
    if (USE_OPT(tinyCtr)) {
        var b = tinyCtr.getBody(methodInfo);
        if (typeof b === 'function') {
            Stat.drop();
            return {
                names: [],
                compiled: b
            };
        }
    }
    var meta = methodInfo.meta;
    var methodName = meta.name;
    var _d = analyze(methodInfo), error = _d.error, jumps = _d.jumps, catchStart = _d.catchStart, catchEnd = _d.catchEnd, q = _d.set;
    // if affilate a generate error, broadcast it
    if (error) {
        Stat.drop();
        // if a error is not debuggable, drop compilation
        if (error.reason !== "underrun" /* UNDERRUN */ || !(optimise & 32 /* DEBUG_UNDERRUN */)) {
            return { error: error };
        }
    }
    var instanceInfo = (methodInfo.instanceInfo || ((_a = methodInfo.trait) === null || _a === void 0 ? void 0 : _a.holder));
    var abc = methodInfo.abc;
    var body = methodInfo.getBody();
    var maxstack = body.maxStack;
    var maxlocal = body.localCount - 1;
    var maxscope = body.maxScopeDepth - body.initScopeDepth;
    var js0 = state.headerBlock;
    var js = state.mainBlock;
    var domMem = false;
    for (var _i = 0, q_1 = q; _i < q_1.length; _i++) {
        var q_i = q_1[_i];
        var b = q_i.name;
        domMem = domMem || (b >= Bytecode.LI8 && b <= Bytecode.SF64);
    }
    state.moveIndent(1);
    var params = methodInfo.parameters;
    var useESArguments = optimise & 1 /* USE_ES_PARAMS */;
    var _e = useESArguments
        ? emitAnnotation(state)
        : emitAnnotationOld(state), paramsShift = _e.paramsShift, annotation = _e.annotation;
    // store indend after annotation
    var namesIndent = state.indent;
    js0.push(annotation);
    var LOCALS_POS = js0.length;
    // hack
    js0.push('__PLACE__FOR__OPTIONAL__LOCALS__');
    var optionalLocalVars = [];
    for (var i = params.length + 1 + paramsShift; i <= maxlocal; i++) {
        optionalLocalVars[i] = {
            index: i,
            isArgumentList: i === params.length + 1,
            read: 0,
            write: 0,
            die: false,
        };
    }
    for (var i = 0; i < maxstack; i++)
        js0.push(namesIndent + "let stack" + i + " = undefined;");
    for (var i = 0; i < maxscope; i++)
        js0.push(namesIndent + "let scope" + i + " = undefined;");
    js0.push(namesIndent + "let temp = undefined;");
    if (domMem)
        js0.push(namesIndent + "let domainMemory; // domainMemory");
    var names = state.names;
    var getname = function (n) { return emitInlineMultiname(state, state.getMultinameIndex(n)); };
    js0.push(namesIndent + "let sec = context.sec;");
    var genBrancher = jumps.length > 1 || catchStart;
    if (METHOD_HOOKS && METHOD_HOOKS[meta.classPath + '__begin']) {
        state.emitMain('/* ATTACH METHOD HOOK */');
        state.emitMain("context.executeHook(" + emitInlineLocal(state, 0) + ", '" + (meta.classPath + '__begin') + "')");
    }
    state.emitMain('');
    var catches = catchStart
        ? Object.keys(catchStart).length
        : 0;
    var useLoopGuard = (Settings.ENABLE_LOOP_GUARD
        && genBrancher
        // every cathch has 2 jumps, ignore it
        && (jumps.length - catches * 2) >= Settings.LOOP_GUARD_MIN_BRANCHES);
    if (genBrancher) {
        if (useLoopGuard) {
            state.emitMain('let tick = 0;');
        }
        state.emitMain('let p = 0;');
        state.emitBeginMain('while (true) {'); // add { automatically
        if (useLoopGuard) {
            var loops = Settings.LOOP_GUARD_MAX_LOOPS;
            state.emitBeginMain("if (tick++ > " + loops + ") {");
            state.emitMain(
            // eslint-disable-next-line max-len
            "throw 'To many loops (> " + loops + ") in \"" + meta.classPath + "\" at' + p + ' ,method was dropped to avoid stucking';\n'");
            state.emitEndMain();
        }
        state.emitBeginMain('switch (p) {');
    }
    var currentCatchBlocks;
    var lastZ;
    var z;
    // case + case int
    genBrancher && state.moveIndent(2);
    var stackF = function (n, alias) {
        if (alias === void 0) { alias = true; }
        return emitInlineStack(state, n, alias);
    };
    var local = function (n) { return emitInlineLocal(state, n); };
    var param = function (n, oppcode) {
        var p = oppcode || state.currentOpcode.params;
        return typeof p === 'number' ? p : p[n];
    };
    for (var i = 0; i < q.length; i++) {
        // store oppcode in state
        state.currentOpcode = q[i];
        z && (lastZ = z);
        z = q[i];
        USE_OPT(fastCall) && fastCall.killFar(i);
        if (jumps.indexOf(z.position) >= 0) {
            // drop aliases for stack, because branching, alias outside branch can be invalid
            state.dropAllAliases();
            // if we are in any try-catch-blocks, we must close them
            //if (state.openTryCatchGroups)
            state.openTryCatchGroups.forEach(function (e) { return emitCloseTryCatch(state, e); });
            if (genBrancher) {
                state.moveIndent(-1);
                state.emitMain("case " + z.position + ":");
                state.moveIndent(1);
            }
            // now we reopen all the try-catch again
            state.openTryCatchGroups.forEach(function (e) { return emitOpenTryCatch(state, e); });
        }
        /* DIRTY */
        currentCatchBlocks = catchStart ? catchStart[z.position] : null;
        if (currentCatchBlocks) {
            state.openTryCatchGroups.push(currentCatchBlocks);
            state.emitBeginMain('try {');
            state.moveIndent(1);
        }
        if (Settings.PRINT_BYTE_INSTRUCTION) {
            state.emitMain("//" + BytecodeName[z.name] + " " + (typeof z.params === 'number' ? z.params : z.params.join(' / ')) + " -> " + z.returnTypeId);
        }
        if (z.comment) {
            state.emitMain("//" + z.comment);
        }
        var stack0 = stackF(0);
        var stack1 = stackF(1);
        var stack2 = stackF(2);
        var stack3 = stackF(3);
        var scope = z.scope > 0 ? "scope" + (z.scope - 1) : 'context.savedScope';
        var scopeN = 'scope' + z.scope;
        if (z.stack < 0) {
            state.emitMain('// unreachable');
        }
        else {
            var localIndex = 0;
            switch (z.name) {
                case Bytecode.LABEL:
                    break;
                case Bytecode.DXNSLATE:
                    state.emitMain(scope + ".defaultNamespace = context.internNamespace(0, " + stack0 + ");");
                    break;
                case Bytecode.DEBUGFILE:
                    break;
                case Bytecode.DEBUGLINE:
                    break;
                case Bytecode.DEBUG:
                    break;
                case Bytecode.THROW:
                    state.emitMain("throw " + stack0 + ";");
                    break;
                case Bytecode.GETLOCAL: {
                    localIndex = param(0);
                    optionalLocalVars[localIndex] && (optionalLocalVars[localIndex].read++);
                    // going onto state, we have a lot of test that allow inlining
                    state.emitGetLocal(-1, localIndex);
                    break;
                }
                case Bytecode.SETLOCAL:
                    localIndex = param(0);
                    if (optionalLocalVars[localIndex]) {
                        optionalLocalVars[localIndex].write++;
                        if (!optionalLocalVars[localIndex].read) {
                            optionalLocalVars[localIndex].die = true;
                        }
                    }
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(local(localIndex));
                    state.emitMain(local(localIndex) + " = " + stack0 + ";");
                    // this is unpossible, because AVM not store `this` in local another that 0
                    /*
                    if (state.isThisAlias(stack0)) {
                        state.pushThisAlias(local(localIndex));
                    }
                    */
                    break;
                case Bytecode.GETSLOT:
                    state.popAnyAlias(stack0);
                    // slots can be get/set only on AX objects
                    state.emitMain(stackF(0, false) + " = " + stack0 + ".axGetSlot(" + param(0) + ");");
                    break;
                case Bytecode.SETSLOT:
                    state.emitMain(stack1 + ".axSetSlot(" + param(0) + ", " + stack0 + ");");
                    break;
                case Bytecode.GETGLOBALSCOPE:
                    state.popAnyAlias(stackF(-1, false));
                    state.emitMain(stackF(-1, false) + " = context.savedScope.global.object;");
                    break;
                case Bytecode.PUSHSCOPE:
                    (_b = staticHoistLex) === null || _b === void 0 ? void 0 : _b.markScope(scopeN, js.length);
                    // extends can be used only on AXObject
                    state.emitMain(scopeN + " = " + scope + ".extend(" + stack0 + ");");
                    break;
                case Bytecode.PUSHWITH:
                    state.emitMain(scopeN + " = context.pushwith(" + scope + ", " + stack0 + ");");
                    break;
                case Bytecode.POPSCOPE:
                    state.emitMain(scope + " = undefined;");
                    break;
                case Bytecode.GETSCOPEOBJECT:
                    state.popAnyAlias(stackF(-1, false));
                    state.emitMain(stackF(-1) + " = scope" + param(0) + ".object;");
                    break;
                case Bytecode.NEXTNAME:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = sec.box(" + stack1 + ").axNextName(" + stack0 + ");");
                    break;
                case Bytecode.NEXTVALUE:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = sec.box(" + stack1 + ").axNextValue(" + stack0 + ");");
                    break;
                case Bytecode.HASNEXT:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = sec.box(" + stack1 + ").axNextNameIndex(" + stack0 + ");");
                    break;
                case Bytecode.HASNEXT2:
                    state.popAnyAlias(stackF(-1, false));
                    state.emitMain("temp = context.hasnext2(" + local(param(0)) + ", " + local(param(1)) + ");");
                    state.emitMain(local(param(0)) + " = temp[0];");
                    state.emitMain(local(param(1)) + " = temp[1];");
                    state.emitMain(stackF(-1) + " = " + local(param(1)) + " > 0;");
                    break;
                case Bytecode.IN:
                    state.popAnyAlias(stackF(1, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(1) + " = (" + stack1 + " && " + stack1 + ".axClass === sec.AXQName) ? obj.axHasProperty(" + stack1 + ".name) : " + stack0 + ".axHasPublicProperty(" + stack1 + ");");
                    break;
                case Bytecode.DUP:
                    state.popAnyAlias(stackF(-1, false));
                    state.emitMain(stackF(-1) + " = " + stack0 + ";");
                    state.pushThisAlias(stackF(-1), stack0);
                    break;
                case Bytecode.POP:
                    // it real pop stack0 ?
                    state.popAnyAlias(stackF(0, false));
                    //js.push(`${idnt};`)
                    break;
                case Bytecode.SWAP: {
                    state.popAnyAlias(stackF(0, false));
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain("temp = " + stack0 + ";");
                    state.emitMain(stackF(0) + " = " + stack1 + ";");
                    state.emitMain(stackF(1) + " = temp;");
                    state.emitMain('temp = undefined;');
                    break;
                }
                case Bytecode.PUSHTRUE:
                    state.emitConst(-1, true);
                    break;
                case Bytecode.PUSHFALSE:
                    state.emitConst(-1, false);
                    break;
                case Bytecode.PUSHBYTE:
                    state.emitConst(-1, param(0));
                    break;
                case Bytecode.PUSHSHORT:
                    state.emitConst(-1, param(0));
                    break;
                case Bytecode.PUSHINT:
                    state.emitConst(-1, abc.ints[param(0)]);
                    break;
                case Bytecode.PUSHUINT:
                    state.emitConst(-1, abc.uints[param(0)]);
                    break;
                case Bytecode.PUSHDOUBLE:
                    state.emitConst(-1, abc.doubles[param(0)]);
                    break;
                case Bytecode.PUSHSTRING:
                    state.emitConst(-1, abc.getString(param(0)));
                    break;
                case Bytecode.PUSHNAN:
                    state.emitConst(-1, NaN);
                    break;
                case Bytecode.PUSHNULL:
                    state.emitConst(-1, null);
                    break;
                case Bytecode.PUSHUNDEFINED:
                    state.emitConst(-1, undefined);
                    break;
                case Bytecode.IFEQ: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " == " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " == " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFNE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " != " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " != " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFSTRICTEQ: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " === " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " === " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFSTRICTNE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " !== " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " !== " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFNLE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (!(" + stack0 + " >= " + stack1 + ")) { return; }");
                        break;
                    }
                    state.emitMain("if (!(" + stack0 + " >= " + stack1 + ")) { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFGT: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " < " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " < " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFNLT: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (!(" + stack0 + " > " + stack1 + ")) { return; }");
                        break;
                    }
                    state.emitMain("if (!(" + stack0 + " > " + stack1 + ")) { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFGE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " <= " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " <= " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFNGE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (!(" + stack0 + " <= " + stack1 + ")) { return; }");
                        break;
                    }
                    state.emitMain("if (!(" + stack0 + " <= " + stack1 + ")) { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFLT: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " > " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " > " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFNGT: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (!(" + stack0 + " < " + stack1 + ")) { return; }");
                        break;
                    }
                    state.emitMain("if (!(" + stack0 + " < " + stack1 + ")) { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFLE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + " >= " + stack1 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + " >= " + stack1 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFFALSE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (!" + stack0 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (!" + stack0 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.IFTRUE: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain("if (" + stack0 + ") { return; }");
                        break;
                    }
                    state.emitMain("if (" + stack0 + ") { p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.LOOKUPSWITCH: {
                    var jj = z.params.concat();
                    var dj = jj.shift();
                    // eslint-disable-next-line max-len
                    state.emitMain("if (" + stack0 + " >= 0 && " + stack0 + " < " + jj.length + ") { p = [" + jj.join(', ') + "][" + stack0 + "]; continue; } else { p = " + dj + "; continue; };");
                    break;
                }
                case Bytecode.JUMP: {
                    if (isFastReturnVoid(q, jumps, i, param(0))) {
                        state.emitMain('//JIT: Emit inline return');
                        state.emitMain('return;');
                        break;
                    }
                    state.emitMain("{ p = " + param(0) + "; continue; };");
                    break;
                }
                case Bytecode.INCREMENT:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + "++;");
                    break;
                case Bytecode.DECREMENT:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + "--;");
                    break;
                case Bytecode.INCLOCAL:
                    state.emitMain(local(param(0)) + "++;");
                    break;
                case Bytecode.DECLOCAL:
                    state.emitMain(local(param(0)) + "--;");
                    break;
                case Bytecode.INCREMENT_I:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = (" + stackF(0) + " | 0) + 1;");
                    break;
                case Bytecode.DECREMENT_I:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = (" + stackF(0) + " | 0) - 1;");
                    break;
                case Bytecode.INCLOCAL_I:
                    state.emitMain(local(param(0)) + " = (" + local(param(0)) + " | 0) + 1;");
                    break;
                case Bytecode.DECLOCAL_I:
                    state.emitMain(local(param(0)) + " = (" + local(param(0)) + " | 0) - 1;");
                    break;
                case Bytecode.NEGATE_I:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = -(" + stack0 + " | 0);");
                    break;
                case Bytecode.ADD_I:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = (" + stack1 + " | 0) + (" + stack0 + " | 0);");
                    break;
                case Bytecode.SUBTRACT_I:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = (" + stack1 + " | 0) - (" + stack0 + " | 0);");
                    break;
                case Bytecode.MULTIPLY_I:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = (" + stack1 + " | 0) * (" + stack0 + " | 0);");
                    break;
                case Bytecode.ADD:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    // LOL, this can be used when this used in string concation
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " += " + stack0 + ";");
                    break;
                case Bytecode.SUBTRACT:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " -= " + stack0 + ";");
                    break;
                case Bytecode.MULTIPLY:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " *= " + stack0 + ";");
                    break;
                case Bytecode.DIVIDE:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " /= " + stack0 + ";");
                    break;
                case Bytecode.MODULO:
                    state.killConstAliasInstruction([stackF(0, false)]);
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " %= " + stack0 + ";");
                    break;
                case Bytecode.LSHIFT:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " <<= " + stack0 + ";");
                    break;
                case Bytecode.RSHIFT:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " >>= " + stack0 + ";");
                    break;
                case Bytecode.URSHIFT:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " >>>= " + stack0 + ";");
                    break;
                case Bytecode.BITAND:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " &= " + stack0 + ";");
                    break;
                case Bytecode.BITOR:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " |= " + stack0 + ";");
                    break;
                case Bytecode.BITXOR:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " ^= " + stack0 + ";");
                    break;
                case Bytecode.EQUALS:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack1 + " == " + stack0 + ";");
                    break;
                case Bytecode.STRICTEQUALS:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack1 + " === " + stack0 + ";");
                    break;
                case Bytecode.GREATERTHAN:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack1 + " > " + stack0 + ";");
                    break;
                case Bytecode.GREATEREQUALS:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack1 + " >= " + stack0 + ";");
                    break;
                case Bytecode.LESSTHAN:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack1 + " < " + stack0 + ";");
                    break;
                case Bytecode.LESSEQUALS:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack1 + " <= " + stack0 + ";");
                    break;
                case Bytecode.NOT:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = !" + stack0 + ";");
                    break;
                case Bytecode.BITNOT:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = ~" + stack0 + ";");
                    break;
                case Bytecode.NEGATE:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = -" + stack0 + ";");
                    break;
                case Bytecode.TYPEOF:
                    state.popAnyAlias(stackF(0, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(0) + " = typeof " + stack0 + " === 'undefined' ? 'undefined' : context.typeof(" + stack0 + ");");
                    break;
                case Bytecode.INSTANCEOF:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack0 + ".axIsInstanceOf(" + stack1 + ");");
                    break;
                case Bytecode.ISTYPE:
                    state.popAnyAlias(stackF(0, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(0) + " = " + scope + ".getScopeProperty(" + getname(param(0)) + ", true, false).axIsType(" + stack0 + ");");
                    break;
                case Bytecode.ISTYPELATE:
                    state.popAnyAlias(stackF(1, false));
                    state.emitMain(stackF(1) + " = " + stack0 + ".axIsType(" + stack1 + ");");
                    break;
                case Bytecode.ASTYPE:
                    if ((optimise & 4 /* SKIP_NULL_COERCE */)
                        && (lastZ.name === Bytecode.PUSHNULL
                            || lastZ.name === Bytecode.PUSHUNDEFINED)) {
                        state.emitMain('// SKIP_NULL_COERCE');
                        break;
                    }
                    state.popAnyAlias(stackF(0, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(0) + " = " + scope + ".getScopeProperty(" + getname(param(0)) + ", true, false).axAsType(" + stack0 + ");");
                    break;
                case Bytecode.ASTYPELATE:
                    state.popAnyAlias(stackF(1, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(1) + " = " + emitIsAXOrPrimitive(stack1) + " ? " + stack0 + ".axAsType(" + stack1 + ") : " + stack1 + ";");
                    break;
                case Bytecode.CALL: {
                    state.popAnyAlias(stackF(param(0) + 1, false));
                    var pp = [];
                    var obj = stackF(param(0) + 1);
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    // eslint-disable-next-line max-len
                    state.emitMain(obj + " = context.call(" + stackF(param(0) + 1) + ", " + stackF(param(0)) + ", [" + pp.join(', ') + "], " + scope + ");");
                    break;
                }
                case Bytecode.CONSTRUCT: {
                    state.popAnyAlias(stackF(param(0), false));
                    var pp = [];
                    var obj = stackF(param(0));
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    var alias = state.getStackAlias(param(0));
                    if (alias && (alias.kind === "lookup" /* LOOKUP */ || alias.kind === "alias" /* ALIAS */) && alias.type) {
                        state.emitMain('//JIT: Possible source:' + alias.type);
                        if (alias.type.name === 'RegExp') {
                            state.emitMain(obj + " = context.getRegExp([" + pp.join(', ') + "]);");
                            break;
                        }
                    }
                    // eslint-disable-next-line max-len
                    state.emitMain(obj + " = context.construct(" + obj + ", [" + pp.join(', ') + "]);");
                    break;
                }
                case Bytecode.CALLPROPERTY: {
                    var mn = abc.getMultiname(param(1));
                    var pp = [];
                    var obj = stackF(param(0));
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    state.killConstAliasInstruction([stackF(param(0), false)]);
                    state.popAnyAlias(stackF(param(0), false));
                    var targetStack = stackF(param(0));
                    if (abc.getMultiname(param(1)).name == 'getDefinitionByName') {
                        // eslint-disable-next-line max-len
                        state.emitMain(targetStack + " = context.getdefinitionbyname(" + scope + ", " + obj + ", [" + pp.join(', ') + "]);");
                    }
                    else {
                        var d = void 0;
                        if (USE_OPT(fastCall) && (d = fastCall.sureThatFast("" + obj, mn.getMangledName()))) {
                            var n = d.isMangled ? Multiname.getPublicMangledName(mn.name) : mn.name;
                            fastCall.kill("" + obj);
                            state.emitMain('/* We sure that this safe call */');
                            if (d.isFunc) {
                                state.emitMain(targetStack + " = " + emitAccess(obj, n) + "(" + pp.join(', ') + ");");
                            }
                            else {
                                // eslint-disable-next-line max-len
                                state.emitMain(targetStack + " = /*fast*/sec.box(" + obj + ").axCallProperty(" + getname(param(1)) + ", [" + pp.join(', ') + "], false);");
                            }
                            break;
                        }
                        // we can check trite for `this` or any types that has trite
                        // @todo move this to fast-call
                        if (Settings.CHEK_TRAIT_GET_CALL && obj === 'this' && instanceInfo) {
                            var trait = resolveTrait(instanceInfo, mn);
                            if (trait) {
                                // eslint-disable-next-line max-len
                                state.emitMain("/* We sure that this safe call, represented in TRAIT as " + TRAITNames[trait.kind] + "  */ ");
                                if (trait.kind === 1 /* Method */) {
                                    // eslint-disable-next-line max-len
                                    state.emitMain(targetStack + " = " + emitAccess(obj, mn.getMangledName()) + "(" + pp.join(', ') + ");");
                                }
                                else {
                                    // when is method, we should wrap caller, because JS miss `this`
                                    // when method is used as outside object
                                    // we can do with BIND, but this is can be unstable
                                    // eslint-disable-next-line max-len
                                    state.emitMain(targetStack + " = /*fast*/" + obj + ".axCallProperty(" + getname(param(1)) + ", [" + pp.join(', ') + "], false);");
                                }
                                break;
                            }
                        }
                        var fast = needFastCheck() && (obj !== 'this' || !Settings.NO_CHECK_FASTCALL_FOR_THIS);
                        if (fast) {
                            state.emitMain("if (!" + emitIsAXOrPrimitive(obj) + ") {");
                            // fast instruction already binded
                            state.emitMain("   " + targetStack + " = " + emitAccess(obj, mn.name) + "(" + pp.join(', ') + ");");
                            state.emitBeginMain('} else {');
                        }
                        state.emitMain("// " + mn);
                        state.emitBeginMain(); // {
                        var box = !Settings.NO_CHECK_BOXED_THIS || obj !== 'this';
                        if (box) {
                            state.emitMain("let t = " + obj + ";");
                            var accessor = emitAccess('t', '$Bg' + mn.name);
                            // eslint-disable-next-line max-len
                            state.emitMain("const m = " + accessor + " || (t = sec.box(" + obj + "), " + accessor + ");");
                        }
                        else {
                            state.emitMain("const m = " + emitAccess(obj, '$Bg' + mn.name) + ";");
                        }
                        state.emitMain('if( typeof m === "function" ) { ');
                        // eslint-disable-next-line max-len
                        state.emitMain("    " + targetStack + " = " + emitAccess(box ? 't' : obj, '$Bg' + mn.name) + " (" + pp.join(', ') + ");");
                        state.emitMain('} else { ');
                        // eslint-disable-next-line max-len
                        state.emitMain("    " + targetStack + " = " + obj + ".axCallProperty(" + getname(param(1)) + ", [" + pp.join(', ') + "], false);");
                        state.emitMain('}');
                        state.emitEndMain(); // }
                        if (fast) {
                            state.emitEndMain(); // }
                        }
                    }
                    break;
                }
                case Bytecode.CALLPROPLEX:
                    {
                        var mn = abc.getMultiname(param(1));
                        state.popAnyAlias(stackF(param(0), false));
                        var targetStack = stackF(param(0));
                        var pp = [];
                        for (var j = 1; j <= param(0); j++) {
                            pp.push(stackF(param(0) - j));
                            state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                        }
                        state.emitMain("temp = sec.box(" + targetStack + ");");
                        var accessor = emitAccess('temp', '$Bg' + mn.name);
                        // eslint-disable-next-line max-len
                        state.emitMain(targetStack + " = (typeof " + accessor + " === 'function')? " + accessor + "(" + pp.join(', ') + ") : temp.axCallProperty(" + getname(param(1)) + ", [" + pp.join(', ') + "], true);");
                    }
                    break;
                case Bytecode.CALLPROPVOID:
                    {
                        var mn = abc.getMultiname(param(1));
                        var pp = [];
                        var obj = stackF(param(0));
                        for (var j = 1; j <= param(0); j++) {
                            pp.push(stackF(param(0) - j));
                            state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                        }
                        state.killConstAliasInstruction([stackF(param(0), false)]);
                        {
                            if (USE_OPT(fastCall) && fastCall.sureThatFast(obj)) {
                                var n = fastCall.sureThatFast(obj).isMangled
                                    ? Multiname.getPublicMangledName(mn.name)
                                    : mn.name;
                                state.emitMain('/* We sure that this safe call */ ');
                                state.emitMain(emitAccess(obj, n) + "(" + pp.join(', ') + ");");
                                fastCall.kill("" + obj);
                                break;
                            }
                        }
                        // we can check trite for `this` or any types that has trite
                        // @todo move this to fast-call
                        if (Settings.CHEK_TRAIT_GET_CALL && obj === 'this' && instanceInfo) {
                            var trait = resolveTrait(instanceInfo, mn);
                            if (trait) {
                                // eslint-disable-next-line max-len
                                state.emitMain("/* We sure that this safe call, represented in TRAIT as " + TRAITNames[trait.kind] + "  */ ");
                                if (trait.kind === 1 /* Method */) {
                                    // eslint-disable-next-line max-len
                                    state.emitMain(emitAccess(obj, mn.getMangledName()) + "(" + pp.join(', ') + ");");
                                }
                                else {
                                    // when is method, we should wrap caller, because JS miss `this`
                                    // when method is used as outside object
                                    // we can do with BIND, but this is can be unstable
                                    // eslint-disable-next-line max-len
                                    state.emitMain("/*fast*/" + obj + ".axCallProperty(" + getname(param(1)) + ", [" + pp.join(', ') + "], false);");
                                }
                                break;
                            }
                        }
                        var fast = needFastCheck() && (obj !== 'this' || !Settings.NO_CHECK_FASTCALL_FOR_THIS);
                        if (fast) {
                            state.emitMain("if (!" + emitIsAXOrPrimitive(obj) + ") {");
                            state.emitMain("    " + emitAccess(obj, mn.name) + "(" + pp.join(', ') + ");");
                            state.emitMain('} else {');
                            state.moveIndent(1);
                        }
                        state.emitMain("// " + mn);
                        state.emitBeginMain(); // {
                        state.emitMain("let t = " + obj + ";");
                        var accessor = emitAccess('t', '$Bg' + mn.name);
                        state.emitMain("const m = " + accessor + " || (t = sec.box(" + obj + "), " + accessor + ");");
                        state.emitMain('if( typeof m === "function" ) { ');
                        state.emitMain("    m.call(t" + (pp.length ? ', ' : '') + pp.join(', ') + ");");
                        state.emitMain('} else { ');
                        state.emitMain("   " + obj + ".axCallProperty(" + getname(param(1)) + ", [" + pp.join(', ') + "], false);");
                        state.emitMain('}');
                        state.emitEndMain(); // }
                        if (fast) {
                            state.emitEndMain(); // }
                        }
                    }
                    break;
                case Bytecode.APPLYTYPE: {
                    var pp = [];
                    state.popAnyAlias(stackF(param(0), false));
                    var targetStack = stackF(param(0));
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    state.emitMain(targetStack + " = sec.applyType(" + targetStack + ", [" + pp.join(', ') + "]);");
                    break;
                }
                case Bytecode.FINDPROPSTRICT: {
                    var mn = abc.getMultiname(param(0));
                    state.emitMain("// " + mn);
                    state.popAnyAlias(stackF(-1, false));
                    var target = stackF(-1);
                    if (USE_OPT(lexGen) && lexGen.test(mn, false)) {
                        state.emitMain('/* GenerateLexImports */');
                        // eslint-disable-next-line max-len
                        var lexAlias = lexGen.getPropStrictAlias(mn, {
                            //nameAlias: getname(param(0)),
                            mnIndex: state.getMultinameIndex(param(0))
                            /*findProp: true,*/
                        });
                        //state.emitMain(`${target} = ${lexAlias};`);
                        // alias as const, this allow inline it
                        state.emitConst(-1, lexAlias, false);
                        if (USE_OPT(fastCall)) {
                            var mangled = (lexGen.getGenerator(mn, false) instanceof TopLevelLex);
                            fastCall.mark(target, i, mangled, mn);
                        }
                        break;
                    }
                    // generate fast lookup for get/call
                    // scope === 1 is extended scope
                    if (Settings.CHEK_TRAIT_GET_CALL
                        && Settings.CHEK_TRAIT_FIND_PROP
                        && z.scope === 1
                        && instanceInfo) {
                        var trait = resolveTrait(instanceInfo, mn);
                        if (trait) {
                            // eslint-disable-next-line max-len
                            state.emitMain("/* We sure that this scope owner, represented in TRAIT as " + TRAITNames[trait.kind] + "  */ ");
                            state.emitGetLocal(-1, 0);
                            break;
                        }
                    }
                    state.emitMain(stackF(-1) + " = " + scope + ".findScopeProperty(" + getname(param(0)) + ", true, false);");
                    break;
                }
                case Bytecode.FINDPROPERTY:
                    state.emitMain("// " + abc.getMultiname(param(0)));
                    state.popAnyAlias(stackF(-1, false));
                    state.emitMain(stackF(-1) + " = " + scope + ".findScopeProperty(" + getname(param(0)) + ", false, false);");
                    break;
                case Bytecode.NEWFUNCTION:
                    state.emitMain("// " + abc.getMethodInfo(param(0)));
                    state.popAnyAlias(stackF(-1, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(-1) + " = context.createFunction(" + param(0) + ", " + scope + ", true, " + methodInfo.index() + ");");
                    break;
                case Bytecode.NEWCLASS:
                    state.emitMain("// " + abc.classes[param(0)]);
                    state.popAnyAlias(stackF(0, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(0) + " = sec.createClass(context.abc.classes[" + param(0) + "], " + stack0 + ", " + scope + ");");
                    break;
                case Bytecode.GETDESCENDANTS:
                    {
                        var mn = abc.getMultiname(param(0));
                        state.emitMain("// " + mn);
                        if (mn.isRuntimeName()) {
                            var runtime = mn.isRuntimeName() && mn.isRuntimeNamespace();
                            state.popAnyAlias(runtime ? stackF(2, false) : stackF(1, false));
                            var target = runtime ? stackF(2) : stackF(1);
                            state.emitBeginMain(); //{
                            if (runtime) {
                                // eslint-disable-next-line max-len
                                state.emitMain("const rn = context.runtimename(" + getname(param(0)) + ", " + stack0 + ", " + stack1 + ");");
                            }
                            else {
                                state.emitMain("const rn = context.runtimename(" + getname(param(0)) + ", " + stack0 + ");");
                            }
                            state.emitMain(target + " = " + target + ".descendants(rn);");
                            state.emitEndMain(); // }
                            break;
                        }
                        else {
                            state.popAnyAlias(stackF(0, false));
                            state.emitMain(stackF(0) + " = " + stack0 + ".descendants(" + getname(param(0)) + ");");
                        }
                        break;
                    }
                case Bytecode.NEWARRAY: {
                    var pp = [];
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    state.popAnyAlias(stackF(param(0) - 1, false));
                    state.emitMain(stackF(param(0) - 1) + " = sec.AXArray.axBox([" + pp.join(', ') + "]);");
                    break;
                }
                case Bytecode.NEWOBJECT: {
                    state.emitMain('temp = Object.create(sec.AXObject.tPrototype);');
                    for (var j = 1; j <= param(0); j++) {
                        // eslint-disable-next-line max-len
                        state.emitMain("temp.axSetPublicProperty(" + stackF(2 * param(0) - 2 * j + 1) + ", " + stackF(2 * param(0) - 2 * j) + ");");
                    }
                    state.popAnyAlias(stackF(2 * param(0) - 1, false));
                    state.emitMain(stackF(2 * param(0) - 1) + " = temp;");
                    state.emitMain('temp = undefined;');
                    break;
                }
                case Bytecode.NEWACTIVATION:
                    state.popAnyAlias(stackF(-1, false));
                    state.emitMain(stackF(-1) + " = sec.createActivation(context.mi, " + scope + ");");
                    break;
                case Bytecode.NEWCATCH:
                    state.popAnyAlias(stackF(-1, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(-1) + " = sec.createCatch(context.mi.getBody().catchBlocks[" + param(0) + "], " + scope + ");");
                    break;
                case Bytecode.CONSTRUCTSUPER:
                    {
                        var pp = [];
                        for (var j = 1; j <= param(0); j++) {
                            pp.push(stackF(param(0) - j));
                            state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                        }
                        state.emitMain("context.savedScope.superConstructor.call(" + stackF(param(0)) + ", " + pp.join(', ') + ");");
                    }
                    break;
                case Bytecode.CALLSUPER: {
                    var pp = [];
                    state.popAnyAlias(stackF(param(0), false));
                    var targetStack = stackF(param(0));
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    // eslint-disable-next-line max-len
                    state.emitMain(targetStack + " = sec.box(" + targetStack + ").axCallSuper(" + getname(param(1)) + ", context.savedScope, [" + pp.join(', ') + "]);");
                    break;
                }
                case Bytecode.CALLSUPER_DYN: {
                    var pp = [];
                    for (var j = 1; j <= param(0); j++) {
                        pp.push(stackF(param(0) - j));
                        state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                    }
                    var mn = abc.getMultiname(param(1));
                    if (mn.isRuntimeName() && mn.isRuntimeNamespace()) {
                        state.popAnyAlias(stackF(param(0) + 2, false));
                        // eslint-disable-next-line max-len
                        state.emitMain(stackF(param(0) + 2) + " = sec.box(" + stackF(param(0) + 2) + ").axGetSuper(context.runtimename(" + getname(param(1)) + ", " + stackF(param(0)) + ", " + stackF(param(0) + 1) + "), context.savedScope, [" + pp.join(', ') + "]);");
                    }
                    else {
                        state.popAnyAlias(stackF(param(0) + 1, false));
                        // eslint-disable-next-line max-len
                        state.emitMain(stackF(param(0) + 1) + " = sec.box(" + stackF(param(0) + 1) + ").axGetSuper(context.runtimename(" + getname(param(1)) + ", " + stackF(param(0)) + "), context.savedScope, [" + pp.join(', ') + "]);");
                    }
                    break;
                }
                case Bytecode.CALLSUPERVOID:
                    {
                        var pp = [];
                        for (var j = 1; j <= param(0); j++) {
                            pp.push(stackF(param(0) - j));
                            state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                        }
                        // eslint-disable-next-line max-len
                        state.emitMain("sec.box(" + stackF(param(0)) + ").axCallSuper(" + getname(param(1)) + ", context.savedScope, [" + pp.join(', ') + "]);");
                    }
                    break;
                case Bytecode.CONSTRUCTPROP:
                    {
                        var pp = [];
                        var targetStack = stackF(param(0), false);
                        var of = stackF(param(0));
                        // order is important, we should check constant assigment to stack/local
                        // before pop alias for this stack/local,
                        // otherwise we lost a offset and can't remove redundant instruction
                        state.killConstAliasInstruction([targetStack]);
                        state.popAnyAlias(targetStack);
                        state.setStackAlias(param(0), {
                            kind: "lookup" /* LOOKUP */,
                            type: abc.getMultiname(param(1)),
                            scope: -1000,
                        });
                        for (var j = 1; j <= param(0); j++) {
                            pp.push(stackF(param(0) - j));
                            state.killConstAliasInstruction([stackF(param(0) - j, false)]);
                        }
                        var supportFast = false;
                        var trace = [];
                        if (Settings.CHECK_FAST_CONSTRUCTOR) {
                            supportFast = isFastConstructSupport(abc.getMultiname(param(1)), trace);
                        }
                        if (supportFast) {
                            state.emitMain('//JIT: Support fast construct:' + trace.join('/'));
                            // eslint-disable-next-line max-len
                            state.emitMain(stackF(param(0)) + " = context.constructFast(" + of + ", [" + pp.join(', ') + "], " + getname(param(1)) + ");");
                        }
                        else {
                            // eslint-disable-next-line max-len
                            state.emitMain(stackF(param(0)) + " = context.constructprop(" + getname(param(1)) + ", " + of + ", [" + pp.join(', ') + "]);");
                        }
                        USE_OPT(fastCall) && fastCall.kill(stackF(param(0)));
                    }
                    break;
                case Bytecode.GETPROPERTY: {
                    var mn = abc.getMultiname(param(0));
                    var of = stackF(0);
                    var target = stackF(0, false);
                    state.killConstAliasInstruction([target]);
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain("// " + mn);
                    {
                        var d = void 0;
                        if (USE_OPT(fastCall) && (d = fastCall.sureThatFast(of, mn.name))) {
                            var n = d.isMangled ? Multiname.getPublicMangledName(mn.name) : mn.name;
                            fastCall.kill(of);
                            state.emitMain('/* We sure that this safe get */ ');
                            state.emitMain(target + " = " + emitAccess(of, n) + ";");
                            break;
                        }
                    }
                    var info = state.getStackAlias(0);
                    if (info) {
                        state.emitMain('//Possible type:' + info.type);
                    }
                    // we can check trite for `this` or any types that has trite
                    // @todo Move this inside fast-call optimiser
                    if (Settings.CHEK_TRAIT_GET_CALL && stack0 === 'this' && instanceInfo) {
                        var trait = resolveTrait(instanceInfo, mn);
                        if (trait) {
                            // eslint-disable-next-line max-len
                            state.emitMain("/* We sure that this safe get, represented in TRAIT as " + TRAITNames[trait.kind] + ", type: " + trait.typeName + "  */ ");
                            if (trait.kind === 1 /* Method */) {
                                state.emitMain(target + " = this.axGetProperty(" + getname(param(0)) + ");");
                                break;
                            }
                            else if (trait.kind === 0 /* Slot */ ||
                                trait.kind === 7 /* GetterSetter */ ||
                                trait.kind === 2 /* Getter */) {
                                state.emitMain(target + " = " + emitAccess(stack0, mn.getMangledName()) + ";");
                                state.setStackAlias(0, {
                                    kind: "var" /* VAR */,
                                    type: trait.typeName,
                                });
                                break;
                            }
                        }
                    }
                    state.setStackAlias(0);
                    var fast = needFastCheck() && (stack0 !== 'this' || !Settings.NO_CHECK_FASTCALL_FOR_THIS);
                    if (fast) {
                        state.emitMain("if (!" + emitIsAX(stack0) + ") {");
                        state.emitMain("    " + target + " = " + stack0 + "['" + mn.name + "'];");
                        state.emitBeginMain('} else {');
                    }
                    var box = !Settings.NO_CHECK_BOXED_THIS || stack0 !== 'this';
                    if (box) {
                        state.emitMain("temp = " + stack0 + "[AX_CLASS_SYMBOL] ? " + stack0 + " : sec.box(" + stack0 + ");");
                    }
                    state.emitMain(target + " = " + emitAccess(box ? 'temp' : stack0, '$Bg' + mn.name) + ";");
                    state.emitMain("if (" + target + " === undefined || typeof " + target + " === 'function') {");
                    state.emitMain("    " + target + " = " + (box ? 'temp' : stack0) + ".axGetProperty(" + getname(param(0)) + ");");
                    state.emitMain('}');
                    if (fast) {
                        state.emitEndMain();
                    }
                    break;
                }
                case Bytecode.GETPROPERTY_DYN: {
                    var mn = abc.getMultiname(param(0));
                    var runtime = mn.isRuntimeName() && mn.isRuntimeNamespace();
                    state.popAnyAlias(runtime ? stackF(2, false) : stackF(1, false));
                    var target = runtime ? stackF(2) : stackF(1);
                    state.emitMain("// " + mn);
                    state.emitBeginMain(); // {
                    if (runtime) {
                        state.emitMain("const rm = context.runtimename(" + getname(param(0)) + ", " + stack0 + ", " + stack1 + ");");
                        state.emitMain('const simple = rm.numeric ? rm.numericValue : rm.name;');
                    }
                    else {
                        state.emitMain("let simple = " + stack0 + ";");
                    }
                    var box = !Settings.NO_CHECK_BOXED_THIS || target !== 'this';
                    if (box) {
                        state.emitMain("const b_obj = " + target + "[AX_CLASS_SYMBOL] ? " + target + " : sec.box(" + target + ");\n");
                    }
                    else {
                        state.emitMain("const b_obj = " + target);
                    }
                    state.emitMain('if (typeof simple === "number") {');
                    state.emitMain("    " + target + " = b_obj.axGetNumericProperty(simple);");
                    state.emitBeginMain('} else {');
                    state.emitMain(target + " = b_obj['$Bg' + simple];");
                    state.emitMain("if (" + target + " === undefined || typeof " + target + " === 'function') {");
                    if (!runtime) {
                        state.emitMain("    const rm = context.runtimename(" + getname(param(0)) + ", " + stack0 + ");");
                    }
                    state.emitMain("    " + target + " = b_obj.axGetProperty(rm);");
                    state.emitMain('}');
                    state.emitEndMain(); // }
                    state.emitEndMain(); // }
                    break;
                }
                case Bytecode.SETPROPERTY: {
                    var mn = abc.getMultiname(param(0));
                    state.killConstAliasInstruction([stackF(0, false), stackF(1, false)]);
                    state.emitMain("// " + mn);
                    // we can check trite for `this` or any types that has trite
                    // @todo Move this to fast-set optimisator
                    if (Settings.CHEK_TRAIT_SET && stack1 === 'this' && instanceInfo) {
                        var trait = resolveTrait(instanceInfo, mn);
                        if (trait && trait) {
                            // eslint-disable-next-line max-len
                            state.emitMain("/* We sure that this safe set, represented in TRAIT as " + TRAITNames[trait.kind] + ", with type: " + trait.typeName + "  */ ");
                            if (trait.kind === 0 /* Slot */ ||
                                trait.kind === 7 /* GetterSetter */ ||
                                trait.kind === 2 /* Getter */ ||
                                trait.kind === 3 /* Setter */) {
                                //debugger;
                                // eslint-disable-next-line max-len
                                state.emitMain(emitAccess(stack1, mn.getMangledName()) + " = " + emitPrimitiveCoerce(state, 0, trait.typeName, true) + ";");
                                break;
                            }
                        }
                    }
                    var fast = needFastCheck() && (stack1 !== 'this' || !Settings.NO_CHECK_FASTCALL_FOR_THIS);
                    if (fast) {
                        state.emitMain("if (!" + emitIsAX(stack1) + "){");
                        state.emitMain("    " + emitAccess(stack1, mn.name) + " = " + stack0 + ";");
                        state.emitBeginMain('} else {');
                    }
                    state.emitMain("context.setproperty(" + getname(param(0)) + ", " + stack0 + ", " + stack1 + ");");
                    if (fast) {
                        state.emitEndMain();
                    }
                    break;
                }
                case Bytecode.SETPROPERTY_DYN: {
                    var mn = abc.getMultiname(param(0));
                    if (mn.isRuntimeName() && mn.isRuntimeNamespace()) {
                        // eslint-disable-next-line max-len
                        state.emitMain("context.setproperty(context.runtimename(" + getname(param(0)) + ", " + stack1 + ", " + stack2 + "), " + stack0 + ", " + stack3 + ");");
                    }
                    else {
                        // eslint-disable-next-line max-len
                        state.emitMain("context.setproperty(context.runtimename(" + getname(param(0)) + ", " + stack1 + "), " + stack0 + ", " + stack2 + ");");
                    }
                    break;
                }
                case Bytecode.DELETEPROPERTY:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain("// " + abc.getMultiname(param(0)));
                    state.emitMain(stackF(0) + " = context.deleteproperty(" + getname(param(0)) + ", " + stack0 + ");");
                    break;
                case Bytecode.DELETEPROPERTY_DYN: {
                    var mn = abc.getMultiname(param(0));
                    if (mn.isRuntimeName() && mn.isRuntimeNamespace()) {
                        state.popAnyAlias(stackF(2, false));
                        // eslint-disable-next-line max-len
                        state.emitMain(stackF(2) + " = context.deleteproperty(context.runtimename(" + getname(param(0)) + ", " + stack0 + ", " + stack1 + "), " + stack2 + ");");
                    }
                    else {
                        state.popAnyAlias(stackF(1, false));
                        // eslint-disable-next-line max-len
                        state.emitMain(stackF(1) + " = context.deleteproperty(context.runtimename(" + getname(param(0)) + ", " + stack0 + "), " + stack1 + ");");
                    }
                    break;
                }
                case Bytecode.GETSUPER:
                    state.popAnyAlias(stackF(0, false));
                    // eslint-disable-next-line max-len
                    state.emitMain(stackF(0) + " = sec.box(" + stack0 + ").axGetSuper(" + getname(param(0)) + ", context.savedScope);");
                    break;
                case Bytecode.GETSUPER_DYN: {
                    var mn = abc.getMultiname(param(0));
                    if (mn.isRuntimeName() && mn.isRuntimeNamespace()) {
                        state.popAnyAlias(stackF(2, false));
                        // eslint-disable-next-line max-len
                        state.emitMain(stackF(2) + " = sec.box(" + stack2 + ").axGetSuper(context.runtimename(" + getname(param(0)) + ", " + stack0 + ", " + stack1 + "), context.savedScope);");
                    }
                    else {
                        state.popAnyAlias(stackF(1, false));
                        // eslint-disable-next-line max-len
                        state.emitMain(stackF(1) + " = sec.box(" + stack1 + ").axGetSuper(context.runtimename(" + getname(param(0)) + ", " + stack0 + "), context.savedScope);");
                    }
                    break;
                }
                case Bytecode.SETSUPER:
                    // eslint-disable-next-line max-len
                    state.emitMain("sec.box(" + stack1 + ").axSetSuper(" + getname(param(0)) + ", context.savedScope, " + stack0 + ");");
                    break;
                case Bytecode.SETSUPER_DYN: {
                    var mn = abc.getMultiname(param(0));
                    if (mn.isRuntimeName() && mn.isRuntimeNamespace()) {
                        // eslint-disable-next-line max-len
                        state.emitMain("sec.box(" + stack3 + ").axSetSuper(context.runtimename(" + getname(param(0)) + ", " + stack1 + ", " + stack2 + "), context.savedScope, " + stack0 + ");");
                    }
                    else {
                        // eslint-disable-next-line max-len
                        state.emitMain("sec.box(" + stack2 + ").axSetSuper(context.runtimename(" + getname(param(0)) + ", " + stack1 + "), context.savedScope, " + stack0 + ");");
                    }
                    break;
                }
                case Bytecode.GETLEX: {
                    var mn = abc.getMultiname(param(0));
                    state.popAnyAlias(stackF(-1, false));
                    var target = stackF(-1);
                    if (USE_OPT(lexGen) && lexGen.test(mn, true)) {
                        state.emitMain("// " + mn);
                        state.emitMain('/* GenerateLexImports GETLEX */');
                        // eslint-disable-next-line max-len
                        state.emitConst(-1, lexGen.getLexAlias(mn, {
                            mnIndex: state.getMultinameIndex(param(0)),
                            /*findProp: false,*/
                            scope: scope
                        }), false);
                        var aliasDec = state.getStackAlias(-1);
                        aliasDec && (aliasDec.type = mn);
                        if (fastCall) {
                            var mangled = (lexGen.getGenerator(mn, true) instanceof TopLevelLex);
                            fastCall.mark(target, i, mangled, mn, true);
                        }
                        break;
                    }
                    state.setStackAlias(-1, {
                        kind: "lookup" /* LOOKUP */,
                        type: mn,
                        scope: z.scope
                    });
                    // generate fast lookup for get/call
                    // scope === 1 is extended scope
                    if (Settings.CHEK_TRAIT_GET_CALL
                        && Settings.CHEK_TRAIT_FIND_PROP
                        && z.scope === 1
                        && instanceInfo) {
                        var trait = resolveTrait(instanceInfo, mn);
                        if (trait) {
                            // eslint-disable-next-line max-len
                            state.emitMain("/* GETLEX We sure that this safe get, represented in TRAIT as " + TRAITNames[trait.kind] + "  */ ");
                            if (trait.kind === 1 /* Method */) {
                                state.emitMain(target + " = " + emitInlineLocal(state, 0) + ".axGetProperty(" + getname(param(0)) + ");");
                                break;
                            }
                            else if (trait.kind === 0 /* Slot */ ||
                                trait.kind === 7 /* GetterSetter */ ||
                                trait.kind === 2 /* Getter */) {
                                state.emitMain(target + " = " + emitAccess(emitInlineLocal(state, 0), mn.getMangledName()) + ";");
                                break;
                            }
                        }
                    }
                    state.emitMain("// " + mn);
                    state.emitMain("temp = " + scope + ".findScopeProperty(" + getname(param(0)) + ", true, false);");
                    state.emitMain(target + " = " + emitAccess('temp', '$Bg' + mn.name) + ";");
                    state.emitMain("if (" + target + " === undefined || typeof " + target + " === 'function') {");
                    state.emitMain("    " + target + " = temp.axGetProperty(" + getname(param(0)) + ");");
                    state.emitMain('}');
                    break;
                }
                case Bytecode.RETURNVALUE: {
                    if (METHOD_HOOKS && METHOD_HOOKS[meta.classPath + '__return']) {
                        state.emitMain('/* ATTACH METHOD HOOK */');
                        // eslint-disable-next-line max-len
                        state.emitMain("context.executeHook(" + emitInlineLocal(state, 0) + ", '" + (meta.classPath + '__return') + "')");
                    }
                    var typeName = methodInfo.getTypeName();
                    if (!typeName ||
                        Settings.COERCE_RETURN_MODE === "none" /* NONE */) {
                        state.emitMain("return " + stack0 + ";");
                        break;
                    }
                    if (isPrimitiveType(typeName)) {
                        state.emitMain("return " + emitPrimitiveCoerce(state, 0, typeName, true) + ";");
                        break;
                    }
                    if (Settings.COERCE_RETURN_MODE === "all" /* ALL */) {
                        state.emitMain("return context.coerceReturn(" + stack0 + ");");
                        break;
                    }
                    state.emitMain("return " + stack0 + ";");
                    break;
                }
                case Bytecode.RETURNVOID:
                    if (METHOD_HOOKS && METHOD_HOOKS[meta.classPath + '__return']) {
                        state.emitMain('/* ATTACH METHOD HOOK */');
                        // eslint-disable-next-line max-len
                        state.emitMain("context.executeHook(" + emitInlineLocal(state, 0) + ", '" + (meta.classPath + '__return') + "')");
                    }
                    state.emitMain('return;');
                    break;
                case Bytecode.COERCE: {
                    if ((optimise & 4 /* SKIP_NULL_COERCE */)
                        && (lastZ.name === Bytecode.PUSHNULL
                            || lastZ.name === Bytecode.PUSHUNDEFINED)) {
                        state.emitMain('//JIT: SKIP_NULL_COERCE');
                        break;
                    }
                    if (lastZ.name === Bytecode.ASTYPELATE) {
                        state.emitMain('//JIT: SKIP DOUBLED COERCE AFTER ASTYPELATE');
                        break;
                    }
                    state.popAnyAlias(stackF(0, false));
                    // WE MUST EMIT REAL STACK WHEN ASSIGN TO IT,
                    var target = stackF(0);
                    var mn = abc.getMultiname(param(0));
                    // skip coerce for native JS objects
                    state.emitBeginMain("if (" + emitIsAX(target) + ") {");
                    if (Settings.COERCE_MODE == "default" /* DEFAULT */) {
                        // eslint-disable-next-line max-len
                        state.emitMain(target + " = " + scope + ".getScopeProperty(" + getname(param(0)) + ", true, false).axCoerce(" + stack0 + ");");
                    }
                    else {
                        // eslint-disable-next-line max-len
                        state.emitMain("var _e = " + scope + ".getScopeProperty(" + getname(param(0)) + ", true, false);");
                        if (Settings.COERCE_MODE === "soft" /* SOFT */) {
                            // eslint-disable-next-line max-len
                            state.emitMain("_e || console.warn('[" + methodName + "] Coerce Type not found:', " + JSON.stringify(mn.name) + ")");
                        }
                        state.emitMain(target + " = _e ? _e.axCoerce(" + stack0 + ") : " + stack0 + ";");
                    }
                    state.emitEndMain();
                    break;
                }
                case Bytecode.COERCE_A:
                    state.emitMain('');
                    break;
                case Bytecode.COERCE_S:
                    if ((optimise & 4 /* SKIP_NULL_COERCE */)
                        && (lastZ.name === Bytecode.PUSHNULL
                            || lastZ.name === Bytecode.PUSHUNDEFINED)) {
                        state.emitMain('// SKIP_NULL_COERCE');
                        break;
                    }
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = context.axCoerceString(" + stack0 + ");");
                    break;
                case Bytecode.ESC_XELEM:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = context.escXElem(" + stack0 + ");");
                    break;
                case Bytecode.ESC_XATTR:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = context.escXAttr(" + stack0 + ");");
                    break;
                case Bytecode.CONVERT_I:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " |= 0;");
                    break;
                case Bytecode.CONVERT_D:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = +" + stack0 + ";");
                    break;
                case Bytecode.CONVERT_B:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " = !!" + stack0 + ";");
                    break;
                case Bytecode.CONVERT_U:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stackF(0) + " >>>= 0;");
                    break;
                case Bytecode.CONVERT_S:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain("if (typeof " + stack0 + " !== 'string') " + stackF(0) + " = " + stack0 + " + '';");
                    break;
                case Bytecode.CONVERT_O:
                    state.emitMain('');
                    break;
                case Bytecode.CHECKFILTER:
                    state.popAnyAlias(stackF(0, false));
                    state.emitMain(stack0 + " = context.axCheckFilter(sec, " + stack0 + ");");
                    break;
                case Bytecode.KILL:
                    // Really we not requre KILL locals
                    // anyway we not validating a locals after jumps, but this can corrupt obfuscated code
                    // and for us this prevent inlining
                    state.emitMain('// Redundant oppcode KILL, prevent optimisations');
                    //state.popAnyAlias(local(param(0)));
                    //state.emitMain(`${local(param(0))} = undefined;`);
                    break;
                default:
                    if (!(optimise & 8 /* SKIP_DOMAIN_MEM */)) {
                        // not required push oppcode, because state should store active
                        emitDomainMemOppcodes(state);
                    }
                    if ((z.name <= Bytecode.LI8 && z.name >= Bytecode.SF64)) {
                        state.emitMain("//unknown instruction " + BytecodeName[q[i].name]);
                        //console.log(`unknown instruction ${BytecodeName[q[i].name]} (method N${methodInfo.index()})`)
                        return {
                            error: {
                                message: 'unhandled instruction ' + z,
                                reason: "unhandled_instruction" /* UNHANDLED_INSTRUCTION */
                            }
                        };
                    }
            }
        }
        /* DIRTY */
        currentCatchBlocks = catchEnd ? catchEnd[z.position] : null;
        if (currentCatchBlocks) {
            var lastCatchBlocks = state.openTryCatchGroups.pop();
            if (lastCatchBlocks) {
                emitCloseTryCatch(state, lastCatchBlocks);
            }
        }
    }
    if (state.openTryCatchGroups.length > 0) {
        var lastCatchBlocks = state.openTryCatchGroups.pop();
        if (lastCatchBlocks) {
            emitCloseTryCatch(state, lastCatchBlocks);
        }
    }
    if (genBrancher) {
        // close switch
        state.emitEndMain();
        // close while
        state.emitEndMain();
    }
    // close closure
    state.emitEndMain();
    var locals = [];
    for (var _f = 0, optionalLocalVars_1 = optionalLocalVars; _f < optionalLocalVars_1.length; _f++) {
        var l = optionalLocalVars_1[_f];
        if (!l) {
            continue;
        }
        if (l.die) {
            locals.push(namesIndent + "// local" + l.index + " is assigned before read, skip init");
        }
        // todo: this is not 100% correct yet:
        locals.push(namesIndent + "let local" + l.index + " = undefined");
        // for NO_ES mode we will generate REST as copy of arguments
        // TODO: Move this to `emitAnnotationOld`
        if (!useESArguments) {
            if (l.index == params.length + 1 && !l.die) {
                // eslint-disable-next-line max-len
                locals.push("    if(arguments && arguments.length) { local" + l.index + " = context.sec.createArrayUnsafe(Array.from(arguments).slice(" + params.length + ")); }");
                locals.push("    else { local" + l.index + " = context.emptyArray; }");
            }
        }
    }
    if (!state.noHoistMultiname) {
        for (var i = 0, l = this.state.names.length; i < l; i++) {
            state.emitHead("let " + emitInlineMultiname(state, i) + " = context.names[" + i + "];", namesIndent);
        }
    }
    else {
        state.emitHead('let $names = context.names;', namesIndent);
    }
    js0[LOCALS_POS] = locals.join('\n');
    var genHeader = ['const AX_CLASS_SYMBOL = context.AX_CLASS_SYMBOL;'];
    var genBody = [];
    var resulMain = state.mainBlock;
    if (USE_OPT(lexGen)) {
        genHeader.push(lexGen.genHeader(state.indent));
        genBody.push(lexGen.genBody(state.indent));
        // mutated generated codeblock
        resulMain = lexGen.genPost(resulMain);
    }
    var scriptHeader = "/*\n\tIndex: " + meta.index + "\n\tPath:  " + meta.classPath + "\n\tType:  " + meta.type + "\n\tKind:  " + meta.kind + "\n\tSuper: " + (meta.superClass || '-') + "\n\tReturn: " + meta.returnType + "\n*/\n\n";
    var w = scriptHeader +
        genHeader.join('\n') +
        state.headerBlock.join('\n') + '\n' +
        genBody.join('\n') + '\n' +
        resulMain.join('\n');
    var hasError = w.indexOf(UNDERRUN) > -1;
    var compiled = generateFunc(w, meta.filePath);
    var underrunLine = -1;
    if (hasError) {
        underrunLine = w.split('\n').findIndex(function (v) { return v.indexOf(UNDERRUN) >= 0; }) + 3;
    }
    // reset lexer
    USE_OPT(lexGen) && lexGen.reset();
    Stat.end();
    var errorMessage = null;
    if (hasError) {
        errorMessage = {
            message: "STACK UNDERRUN at http://jit/" + meta.filePath + ".js:" + underrunLine,
            reason: "underrun" /* UNDERRUN */,
        };
    }
    return {
        names: names,
        compiled: compiled,
        error: errorMessage
    };
}
var Context = /** @class */ (function () {
    function Context(mi, savedScope, names) {
        /*jit internal*/ this.jsGlobal = jsGlobal;
        /*jit internal*/ this.axCoerceString = axCoerceString;
        /*jit internal*/ this.axCheckFilter = axCheckFilter;
        /*jit internal*/ this.internNamespace = internNamespace;
        this.AX_CLASS_SYMBOL = IS_AX_CLASS;
        this.mi = mi;
        this.savedScope = savedScope;
        this.rn = new Multiname(mi.abc, 0, null, null, null, null, true);
        this.abc = mi.abc;
        this.sec = mi.abc.applicationDomain.sec;
        this.names = names;
        this.emptyArray = Object.create(this.sec.AXArray.tPrototype);
        this.emptyArray.value = [];
    }
    Object.defineProperty(Context.prototype, "domainMemory", {
        get: function () {
            if (!this.domain) {
                this.domain = this.sec.flash.system.ApplicationDomain.axClass.currentDomain;
                if (!this.domain) {
                    console.warn('[JIT] Try access to domainMemory on unresolved ApplicationDomain!');
                    return null;
                }
            }
            return this.domain.internal_memoryView;
        },
        enumerable: true,
        configurable: true
    });
    Context.prototype.getRegExp = function (args) {
        var key = args.join('|');
        return Context.REG_EXP_CACHE[key] ||
            (Context.REG_EXP_CACHE[key] = this.sec.AXRegExp.axConstruct(args));
    };
    /**
     * Coerce returned value to type
     * @param value
     */
    Context.prototype.coerceReturn = function (value) {
        var type = this.mi.getType();
        return type ? type.axCoerce(value) : value;
    };
    Context.prototype.createFunction = function (methodId, scope, hasDynamicScope, parentMethodId) {
        var methodInfo = this.abc.getMethodInfo(methodId);
        if (parentMethodId !== undefined) {
            methodInfo.parentInfo = methodInfo.parentInfo || this.abc.getMethodInfo(parentMethodId);
        }
        return this.sec.createFunction(methodInfo, scope, hasDynamicScope);
    };
    Context.prototype.escXElem = function (value) {
        return escapeElementValue(this.sec, value);
    };
    Context.prototype.escXAttr = function (value) {
        return escapeAttributeValue(value);
    };
    /**
     * Execute JS hook
     */
    Context.prototype.executeHook = function (context, name) {
        var hook = METHOD_HOOKS[name];
        if (hook) {
            hook.hook(context);
        }
    };
    /**
    * Generate static import for builtins
    */
    Context.prototype.getTopLevel = function (mnId, name) {
        var prop = this.savedScope.findScopeProperty(this.names[mnId], true, false);
        if (name) {
            return prop[name];
        }
        return prop;
    };
    /**
     * Generate static import of object
     */
    Context.prototype.getStaticImportExt = function (namespace, name) {
        if (name === void 0) { name = undefined; }
        return getExtClassField(name, namespace);
    };
    Context.prototype.typeof = function (object) {
        var type = typeof object;
        var sec = this.sec;
        switch (type) {
            case 'boolean':
                return 'Boolean';
            case 'object':
                if (object === null) {
                    return 'object';
                }
                if (sec.AXXMLList.dPrototype.isPrototypeOf(object) || sec.AXXML.dPrototype.isPrototypeOf(object)) {
                    return 'xml';
                }
                if (sec.AXNumber.dPrototype.isPrototypeOf(object)) {
                    return 'number';
                }
                if (sec.AXBoolean.dPrototype.isPrototypeOf(object)) {
                    // what???.
                    return 'Boolean';
                }
                if (sec.AXString.dPrototype.isPrototypeOf(object)) {
                    return 'string';
                }
        }
        return type;
    };
    Context.prototype.call = function (value, obj, pp, scope) {
        if (scope === void 0) { scope = null; }
        var _a, _b, _c;
        if (scope && ((_c = (_b = (_a = value.methodInfo) === null || _a === void 0 ? void 0 : _a.trait) === null || _b === void 0 ? void 0 : _b.name) === null || _c === void 0 ? void 0 : _c.name) == 'getDefinitionByName') {
            return this.getdefinitionbyname(scope, obj, pp);
        }
        validateCall(this.sec, value, pp.length);
        return value.axApply(obj, pp);
    };
    Context.prototype.getdefinitionbyname = function (scope, _, args) {
        var info = scope.global.object.scriptInfo;
        // pp can be XMList, that required to conversion
        return info.abc.applicationDomain.getClass(Multiname.FromSimpleName(args[0].toString()));
    };
    Context.prototype.getpropertydyn = function (mn, obj) {
        var b = this.sec.box(obj);
        if (typeof mn === 'number')
            return b.axGetNumericProperty(mn);
        var temp = b['$Bg' + mn.name];
        if (temp != undefined && typeof temp !== 'function')
            return temp;
        return b.axGetProperty(mn);
    };
    Context.prototype.setproperty = function (mn, value, obj) {
        var _a, _b;
        if (obj == void 0) {
            throw this.sec.createError('Error', 
            // eslint-disable-next-line max-len
            "[AVM2] Unexpected property assignment: " + typeof obj + "[" + JSON.stringify((_a = mn) === null || _a === void 0 ? void 0 : _a.name) + "] = " + ((_b = value) === null || _b === void 0 ? void 0 : _b.toString()));
        }
        if (needFastCheck()) {
            // unsafe SET into plain Object
            if (!obj[IS_AX_CLASS]) {
                obj[mn.name] = value;
                return;
            }
            // Hubrid
            // Mom is Human, Dad is Marsian
            // and it not has a axSetProp
            if (obj[IS_EXTERNAL_CLASS]) {
                // create prop and proxy to JS side.
                ASObject.prototype.axSetProperty.call(obj, mn, value, Bytecode.INITPROPERTY);
                Object.defineProperty(obj, mn.name, { value: value, configurable: true, writable: true });
                return;
            }
        }
        if (typeof mn === 'number') {
            return obj.axSetNumericProperty(mn, value);
        }
        obj.axSetProperty(mn, value, Bytecode.INITPROPERTY);
    };
    Context.prototype.deleteproperty = function (name, obj) {
        var b = this.sec.box(obj);
        if (typeof name === 'number' || typeof name === 'string')
            return delete b[name];
        return b.axDeleteProperty(name);
    };
    /**
     * Fast constructor for compile-time knowned external classes, like box2D or nape
     */
    Context.prototype.constructExt = function (ctor, args, mn) {
        mn = mn || ctor.classInfo.instanceInfo.getName();
        return extClassConstructor(mn, args);
    };
    /**
     * Fast constructor for strictly known non-interactive classes, that not required checks
     */
    Context.prototype.constructFast = function (ctor, args, mn) {
        // todo Check this statically, we can check external class name in compile time
        if (needFastCheck()) {
            var extConsructor = this.constructExt(ctor, args, mn);
            if (extConsructor) {
                return extConsructor;
            }
        }
        if (mn) {
            return axConstructFast(ctor[ctor.axResolveMultiname(mn)], args);
        }
        return axConstructFast(ctor, args);
    };
    /**
     * Basic constructor for axObjects, slower that any others
     */
    Context.prototype.construct = function (obj, pp) {
        var mn = obj.classInfo.instanceInfo.getName();
        var r = extClassConstructor(mn, pp);
        if (r != null)
            return r;
        // if (mn.name.indexOf("b2") >= 0)
        //     console.log("*B2: " + mn.name)
        validateConstruct(this.sec, obj, pp.length);
        return obj.axConstruct(pp);
    };
    Context.prototype.constructprop = function (mn, obj, pp) {
        var r = extClassConstructor(mn, pp);
        if (r != null)
            return r;
        // if (mn.name.indexOf("b2") >= 0)
        //     console.log("B2: " + mn.name)
        var b = this.sec.box(obj);
        var name = b.axResolveMultiname(mn);
        var ctor = b[name];
        validateConstruct(b.sec, ctor, pp.length);
        return ctor.axConstruct(pp);
    };
    Context.prototype.pushwith = function (scope, obj) {
        var b = this.sec.box(obj);
        return (scope.object === b && scope.isWith == true) ? scope : new Scope(scope, b, true);
    };
    Context.prototype.hasnext2 = function (obj, name) {
        var info = Context.HAS_NEXT_INFO;
        if (obj == undefined) {
            return [null, 0];
        }
        info.next(obj[IS_AX_CLASS] ? obj : this.sec.box(obj), name);
        return [info.object, info.index];
    };
    Context.prototype.runtimename = function (mn, propName, nsName) {
        this.rn.resolved = {};
        this.rn.script = null;
        this.rn.numeric = false;
        this.rn.id = mn.id;
        this.rn.kind = mn.kind;
        if (mn.isRuntimeName()) {
            var name_1 = propName;
            // so, undef and null is valid keys, but unsafe
            if (name_1 == void 0) {
                console.warn("[AVM2 SetProperty DYN] Key is " + name_1 + ", are you sure that this is valid?");
                name_1 = '' + name_1;
                // Unwrap content script-created AXQName instances.
            }
            else if (typeof name_1 === 'object' && name_1.axClass === this.sec.AXQName) {
                name_1 = name_1.name;
                release || assert(name_1 instanceof Multiname);
                this.rn.kind = mn.isAttribute() ? 18 /* RTQNameLA */ : 17 /* RTQNameL */;
                this.rn.id = name_1.id;
                this.rn.name = name_1.name;
                this.rn.namespaces = name_1.namespaces;
                return this.rn;
            }
            else if (typeof name_1 === 'number') {
                this.rn.numeric = true;
                this.rn.numericValue = name_1;
            }
            else {
                var coerce = axCoerceName(name_1);
                if (isNumeric(coerce)) {
                    this.rn.numeric = true;
                    this.rn.numericValue = +coerce;
                }
            }
            this.rn.name = name_1;
            this.rn.id = -1;
        }
        else {
            this.rn.name = mn.name;
            nsName = propName;
        }
        if (mn.isRuntimeNamespace()) {
            var ns = nsName;
            // Unwrap content script-created AXNamespace instances.
            if (ns._ns) {
                release || assert(ns.sec && ns.axClass === ns.sec.AXNamespace);
                ns = ns._ns;
            }
            this.rn.namespaces = [ns];
            this.rn.id = -1;
        }
        else {
            this.rn.namespaces = mn.namespaces;
        }
        return this.rn;
    };
    Context.REG_EXP_CACHE = {};
    Context.HAS_NEXT_INFO = new HasNext2Info(null, 0);
    return Context;
}());
export { Context };
